import { i18n } from '@quadralize/i18n'
import { parseIsoDate, toIsoDate } from '@quadralize/utils'
import { CloseCircle, Filter } from '@solar-icons/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Combobox } from '../../forms/combobox/combobox'
import { DatePicker } from '../../forms/date-picker/date-picker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../forms/select/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../overlays/sheet/sheet'
import { Button } from '../../ui/button/button'
import { Badge } from '../badge/badge'
import { ScrollArea } from '../scroll-area/scroll-area'
import { clampIsoDate } from './data-table-date-filter.utils'
import type { DataTableFilterDrawerProps } from './data-table-filters.types'
import { getActiveFilterCount } from './data-table-filters.types'

const APPLY_FILTERS_AFTER_CLOSE_DELAY_MS = 240

/**
 * Desktop filter sheet — slides in from the right.
 * Uses a pending-filter pattern just like the mobile DrawerFilter.
 */
export function DataTableFilterSheet({
  filters,
  onClearFilters,
  title,
  description,
  trigger,
  hasSearch,
}: DataTableFilterDrawerProps & { hasSearch?: boolean }) {
  const { t } = useTranslation(['ui', 'common'], { i18n })
  const [open, setOpen] = useState(false)
  const [pendingFilters, setPendingFilters] = useState<Record<string, string>>({})
  const resolvedTitle = title ?? t('dataTable.filters.title', { ns: 'ui' })
  const getAllOptionLabel = (label: string) => {
    if (label === 'Status') return t('dataTable.filters.all', { ns: 'ui' })
    if (label === 'Esporte') return t('dataTable.filters.allSports', { ns: 'ui' })
    if (label === 'Categoria') return t('dataTable.filters.allCategories', { ns: 'ui' })
    if (label === 'Quadra') return 'Todas as quadras'
    if (label === 'Tipo') return 'Todos os tipos'
    return `Todos ${label}`
  }

  // Sync pending filters with actual filters when sheet opens
  useEffect(() => {
    if (open) {
      const initialFilters: Record<string, string> = {}
      filters.forEach((f) => {
        initialFilters[f.id] = String(f.value)
        if (f.type !== 'date') {
          const currentValue = String(f.value)
          f.onPendingValueChange?.(currentValue === 'all' ? 'all' : currentValue)
        }
      })
      setPendingFilters(initialFilters)
    }
  }, [open, filters])

  const activeFilterCount = getActiveFilterCount(filters)
  const hasActiveFilters = activeFilterCount > 0
  const hasPendingChanges = filters.some((filter) => {
    const pendingValue = pendingFilters[filter.id]
    return pendingValue !== undefined && pendingValue !== String(filter.value)
  })
  const showClearButton = hasActiveFilters || hasSearch

  const handleClearFilters = () => {
    onClearFilters()
    setOpen(false)
  }

  const handleApplyFilters = () => {
    filters.forEach((filter) => {
      const pendingValue = pendingFilters[filter.id]
      if (pendingValue !== undefined && pendingValue !== String(filter.value)) {
        if (filter.type === 'date') {
          filter.onValueChange(pendingValue)
          return
        }
        filter.onValueChange(pendingValue === 'all' ? 'all' : pendingValue)
      }
    })
    setOpen(false)
  }

  const handleFilterChange = (filter: (typeof filters)[number], value: string) => {
    setPendingFilters((prev) => ({ ...prev, [filter.id]: value }))
    if (filter.type !== 'date') {
      filter.onPendingValueChange?.(value === 'all' ? 'all' : value)
    }
  }

  const applyFilterAfterClose = (applyFilter: () => void) => {
    setOpen(false)
    window.setTimeout(() => {
      applyFilter()
    }, APPLY_FILTERS_AFTER_CLOSE_DELAY_MS)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)

    if (nextOpen) return

    filters.forEach((filter) => {
      if (filter.type === 'date') return
      const currentValue = String(filter.value)
      filter.onPendingValueChange?.(currentValue === 'all' ? 'all' : currentValue)
    })
  }

  const defaultTrigger = (
    <Button
      variant="outline"
      className="relative h-10 min-h-10 shrink-0 gap-2 overflow-visible [&>div]:overflow-visible [&>div>span]:overflow-visible"
      aria-label={t('dataTable.filters.openFilters', { ns: 'ui' })}
    >
      <Filter className="h-4 w-4" />
      {t('dataTable.filters.title', { ns: 'ui' })}
      {hasActiveFilters && (
        <Badge
          variant="default"
          className="absolute -right-5 -top-3 h-5 min-w-5 rounded-full px-1 text-[10px] leading-none flex items-center justify-center border-2 border-bg-base z-10"
        >
          {activeFilterCount}
        </Badge>
      )}
    </Button>
  )

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>{trigger ?? defaultTrigger}</SheetTrigger>

      <SheetContent className="w-80 sm:w-96" side="right">
        <SheetHeader>
          <SheetTitle>{resolvedTitle}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="flex flex-col gap-5 pl-6 pr-5 py-4">
            {filters.map((filter) => (
              <div key={filter.id} className="flex flex-col gap-2">
                <label
                  htmlFor={`sheet-${filter.id}`}
                  className="text-sm font-medium text-text-primary"
                >
                  {filter.label}
                </label>
                {filter.type === 'date' ? (
                  <DatePicker
                    id={`sheet-${filter.id}`}
                    date={parseIsoDate(pendingFilters[filter.id] || filter.value)}
                    setDate={(date) =>
                      handleFilterChange(
                        filter,
                        clampIsoDate(toIsoDate(date), filter.min, filter.max),
                      )
                    }
                    placeholder={filter.placeholder}
                    minDate={filter.min ? (parseIsoDate(filter.min) ?? undefined) : undefined}
                    isDateDisabled={
                      filter.max
                        ? (date) => {
                            const maxDate = parseIsoDate(filter.max ?? '')
                            return !!maxDate && date > maxDate
                          }
                        : undefined
                    }
                  />
                ) : filter.searchable ? (
                  <Combobox
                    id={`sheet-${filter.id}`}
                    value={
                      (pendingFilters[filter.id] || String(filter.value)) === 'all'
                        ? ''
                        : pendingFilters[filter.id] || String(filter.value)
                    }
                    onValueChange={(val) => {
                      const resolvedValue = val || 'all'

                      if (filter.immediateValues?.includes(resolvedValue)) {
                        filter.onValueChange(resolvedValue === 'all' ? 'all' : resolvedValue)
                        handleFilterChange(filter, resolvedValue)
                        return
                      }

                      if (filter.mobileImmediateValues?.includes(resolvedValue)) {
                        applyFilterAfterClose(() => {
                          filter.onValueChange(resolvedValue === 'all' ? 'all' : resolvedValue)
                        })
                        return
                      }

                      handleFilterChange(filter, resolvedValue)
                    }}
                    options={[
                      ...(filter.showAllOption !== false
                        ? [{ value: 'all', label: getAllOptionLabel(filter.label) }]
                        : []),
                      ...filter.options.map((option) => ({
                        value: String(option.value),
                        label: option.label,
                      })),
                    ]}
                    fetcher={
                      filter.fetchOptions
                        ? async (query) => {
                            const fetchedOptions = await filter.fetchOptions?.(query)
                            return [
                              ...(filter.showAllOption !== false
                                ? [{ value: 'all', label: getAllOptionLabel(filter.label) }]
                                : []),
                              ...(fetchedOptions ?? []).map((option) => ({
                                value: String(option.value),
                                label: option.label,
                              })),
                            ]
                          }
                        : undefined
                    }
                    placeholder={filter.placeholder}
                    searchPlaceholder={filter.searchPlaceholder ?? `Buscar ${filter.label}`}
                    emptyMessage={
                      filter.emptyMessage ?? t('dataTable.filters.noResults', { ns: 'ui' })
                    }
                    clearable
                  />
                ) : (
                  <Select
                    value={pendingFilters[filter.id] || String(filter.value)}
                    onValueChange={(val) => {
                      if (filter.immediateValues?.includes(val)) {
                        filter.onValueChange(val === 'all' ? 'all' : val)
                        handleFilterChange(filter, val)
                        return
                      }

                      if (filter.mobileImmediateValues?.includes(val)) {
                        applyFilterAfterClose(() => {
                          filter.onValueChange(val === 'all' ? 'all' : val)
                        })
                        return
                      }

                      handleFilterChange(filter, val)
                    }}
                  >
                    <SelectTrigger id={`sheet-${filter.id}`}>
                      <SelectValue placeholder={filter.placeholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {filter.showAllOption !== false && (
                        <SelectItem value="all">{getAllOptionLabel(filter.label)}</SelectItem>
                      )}
                      {filter.options.map((option) => (
                        <SelectItem key={String(option.value)} value={String(option.value)}>
                          {option.icon ? (
                            <div className="flex items-center gap-2">
                              {option.icon}
                              <span className="truncate">{option.label}</span>
                            </div>
                          ) : (
                            option.label
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </div>
        </ScrollArea>

        <SheetFooter>
          {showClearButton && (
            <Button variant="outline" onClick={handleClearFilters} startIcon={<CloseCircle />}>
              {t('actions.clearFilters', { ns: 'common' })}
            </Button>
          )}
          <Button
            variant={hasPendingChanges || hasActiveFilters ? 'secondary' : 'default'}
            onClick={handleApplyFilters}
          >
            {hasPendingChanges || hasActiveFilters
              ? t('dataTable.filters.applyFilters', { ns: 'ui' })
              : t('dataTable.filters.close', { ns: 'ui' })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
